import { Grid, Stack, Typography } from "@mui/material";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { MprcLink } from "components";
import { MprcMarkdown } from "components/MprcMarkdown";
import { GlobalSearchAttachDto, KnowledgeSearchDto, PublicationAttachType, PublicationClassificationType } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import LibUtils from "lib/LibUtils";
import MediaUtils from "lib/MediaUtils";
import * as React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { KnowledgePaths } from "routes/KnowledgeRouter";
import { SessionService } from "services/sessionService";
import { UserProfileDialog } from "views/foundation/user/dialogs/UserProfileDialog";

/////////////////////////////////////////////////////////////////////////////////////////////////

export function buildHeadLine(headline: string | null) {
    if (headline && headline.length > 255) {
        // Quebra headline em tokens
        let words = LibUtils.tokenizerString(headline);

        // Descobre em qual palavra comecará a frase.
        let sWordIndex = words.findIndex(p => p.startsWith("<b>"));
        if (sWordIndex < 10) sWordIndex = 0;          // Se está entre as 10 primeiras palavras, começa na primeira palavra.
        else sWordIndex = sWordIndex - 3;             // não começa 3 palavras antes do match.

        // + 20 palavras depois do primeiro match
        let eWordIndex = Math.min(words.length, 20);

        // trunca a frase.
        headline = (sWordIndex > 0 ? "... " : "")
            + words.filter((w, i) => i > sWordIndex && i < eWordIndex).join(' ')
            + (eWordIndex < words.length ? "..." : "");
    }

    return headline;
}

interface IProps {
    data: KnowledgeSearchDto;
    index: number;
    onBeforeLinkClick?: () => void;
}

type Props = IProps;

/////////////////////////////////////////////////////////////////////////////////////////////////

export function translateFileExtension(filename: string) {

    if (MediaUtils.isPDF(filename)) return "GlobalSearch.Filters.Knowledge.AttachDocumentType.Pdf";
    if (MediaUtils.isDocument(filename)) return "GlobalSearch.Filters.Knowledge.AttachDocumentType.Word";
    if (MediaUtils.isSheet(filename)) return "GlobalSearch.Filters.Knowledge.AttachDocumentType.Excel";
    if (MediaUtils.isSlides(filename)) return "GlobalSearch.Filters.Knowledge.AttachDocumentType.PowerPoint";

    return "GlobalSearch.Filters.Knowledge.AttachDocumentType.File";
}

export const SearchRenderPublication: React.FC<Props> = (props) => {

    const navigate = useNavigate();
    const { t } = useTranslation("shared");

    const [searchParams] = useSearchParams();
    const [dialog, setDialog] = useState<any>();
    const { data, index, onBeforeLinkClick } = props;

    const navUrl = KnowledgePaths.PublicationViewerResolver(data.publicationId, data.publicationType, data.linkedPublicationId);
    const headline = buildHeadLine(data.headline ?? data.summary);

    const navToAttach = (attach: GlobalSearchAttachDto) => {
        if (attach.attachType == PublicationAttachType.Video || attach.attachType == PublicationAttachType.YouTube || attach.attachType == PublicationAttachType.Vimeo || attach.attachType == PublicationAttachType.Audio) {
            navigate(navUrl, { state: { attachId: attach.attachId, searchText: searchParams.get("q") } });
        }
        else if (attach.filename && (attach.attachType == PublicationAttachType.Document || attach.attachType == PublicationAttachType.File)) {
            var attachUrl = SessionService.current.getAssetUrl(attach.fileId)!;
            MediaUtils.openFileViewerOrDownloadUrl(attach.filename, attachUrl);
        }
        else {
            navigate(navUrl);
        }
    };

    const navToLink = () => {
        if (onBeforeLinkClick) onBeforeLinkClick();
        if (data.attachs.length > 0 && (data.classificationType == PublicationClassificationType.Video || data.classificationType == PublicationClassificationType.Audio)) {
            navToAttach(data.attachs[0]);
        }
        else {
            navigate(navUrl);
        }
    };

    return (
        <React.Fragment key={"searchItem-" + index}>
            {dialog}
            <Grid container spacing={2}>
                <Grid size="grow">
                    <MprcLink
                        onClick={navToLink}
                    >
                        <Typography variant="body1" color="primary.main">
                            {data.title}
                        </Typography>
                    </MprcLink>

                    <Typography variant="subtitle1">
                        <>
                            {data.classificationTypeName}
                            &nbsp;&bull;&nbsp;
                        </>
                        <MprcLink
                            color="inherit"
                            onClick={() => setDialog(
                                <UserProfileDialog
                                    userId={data.authorId}
                                    onClose={() => setDialog(null)}
                                    onBeforeLinkClick={onBeforeLinkClick}
                                />
                            )}
                        >
                            {data.authorName}
                        </MprcLink>
                        ,&nbsp;
                        {Formaters.relativeTime(data.lastUpdate)}

                        {
                            headline &&
                            <Typography variant="caption" component="div" color="muted.light">
                                <MprcMarkdown>{headline}</MprcMarkdown>
                            </Typography>
                        }

                        {
                            data.attachs.length > 0 &&
                            <Stack sx={{ ml: 3 }} gap={0.25}>
                                {data.attachs.map(attach =>
                                    <Stack direction="row" key={attach.headline} alignItems="flex-start" component="p" sx={{ m: 1 }}>
                                        <Typography
                                            variant="captionHeader"
                                            color="primary.main"
                                            component="span"
                                            className={AppGlobalClassKeys.clickable}
                                            onClick={
                                                () => {
                                                    onBeforeLinkClick && onBeforeLinkClick();
                                                    navToAttach(attach);
                                                }
                                            }
                                        >
                                            {attach.attachType == PublicationAttachType.Document && attach.filename
                                                ? attach.fileTypeName ?? t(translateFileExtension(attach.filename))
                                                : attach.attachTypeName
                                            }
                                            <span>:&nbsp;</span>
                                        </Typography>
                                        <Typography variant="caption" component="span" color="muted.light">
                                            <MprcMarkdown>{attach.headline}</MprcMarkdown>
                                        </Typography>
                                    </Stack>
                                )}
                            </Stack>
                        }
                    </Typography>

                </Grid>

            </Grid>
        </React.Fragment>
    );
};
